/**
 * Email Dialog Component for PDF sharing
 */

import React, { Fragment } from 'react'
import {
  Grid, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,  IconButton, InputLabel, ListItem, ListItemText, MenuItem, Select, TextField, Checkbox } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { Close } from '@mui/icons-material'
import {
  ValidatorForm,
  TextValidator} from '@/housefolios-components/shared/muiFormValidator'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { enqueueSnackbar } from 'notistack'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import Autocomplete from '@mui/material/Autocomplete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { isEmpty, isEqual } from '@/utils/lodash'
import { getStrategyName } from '@/utils/stratagies'
import { useShareEmails } from '@/hooks/useShareEmails'
import ShareEmailsAutocomplete from '@/housefolios-components/FormComponents/ShareEmailsAutocomplete'

const EmailDialog = ({
  open,
  onClose,
  onSubmit,
  state,
  handleChange,
  setEmailImagePage,
  setFlyers,
  property,
  session,
  closeSnackbar }) => {
  const { type, emails, emailImagePage, flyers, backdrop } = state
  const shareEmails = useShareEmails(emails)

  const handleSubmit = async (event) => {
    event.preventDefault()
    const finalEmails = shareEmails.collectEmails()
    if (isEmpty(finalEmails)) {
      enqueueSnackbar('Enter at least one valid email address', {
        variant: 'warning',
        autoHideDuration: 4000,
      })
      return
    }
    handleChange({
      target: {
        name: 'emails',
        value: finalEmails,
      },
    })
    await onSubmit(event, finalEmails)
  }

  const handleEmailImagePageChange = () => {
    setEmailImagePage(!emailImagePage)
  }

  const handleFlyersChange = (event, newValue) => {
    setFlyers(newValue)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">
        Share Proforma
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]}}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <BlockUi
        tag="div"
        blocking={backdrop}
        loader={<PacmanLoader />}
      >
        <ValidatorForm onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={12}>
                <ShareEmailsAutocomplete shareEmails={shareEmails} />
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Proforma Type</InputLabel>
                  <Select
                    name="type"
                    value={type}
                    onChange={handleChange}
                    label="Proforma Type"
                  >
                    <MenuItem value={'buyHold'}>
                      <ListItemText
                        primary={
                          'Proforma - ' +
                          getStrategyName(
                            'Buy & Hold',
                            session?.me?.activeOrg?.member,
                          )
                        }
                      />
                    </MenuItem>
                    <MenuItem value={'fixFlip'}>
                      <ListItemText
                        primary={
                          'Proforma - ' +
                          getStrategyName(
                            'Fix & Flip',
                            session?.me?.activeOrg?.member,
                          )
                        }
                      />
                    </MenuItem>
                    <MenuItem value={'buildToRent'}>
                      <ListItemText
                        primary={
                          'Proforma - ' +
                          (session?.me?.activeOrg?.southernImpressions
                            ? getStrategyName(
                                'Build to Rent',
                                session?.me?.activeOrg?.member,
                              )
                            : 'End Buyer')
                        }
                      />
                    </MenuItem>
                    <MenuItem value={'shortTermRental'}>
                      <ListItemText
                        primary={
                          'Proforma - ' +
                          getStrategyName(
                            'Short Term Rental',
                            session?.me?.activeOrg?.member,
                          )
                        }
                      />
                    </MenuItem>
                    {session?.me?.activeOrg?.member !==
                      'astroflip' && (
                      <MenuItem value={'wholesale'}>
                        <ListItemText
                          primary={
                            'Proforma - ' +
                            getStrategyName(
                              'Wholesale',
                              session?.me?.activeOrg?.member,
                            )
                          }
                        />
                      </MenuItem>
                    )}
                    {session?.me?.activeOrg?.member ===
                      'astroflip' && (
                      <MenuItem value={'astroflip'}>
                        <ListItemText
                          primary={
                            'Proforma - ' +
                            getStrategyName(
                              'Astroflip',
                              session?.me?.activeOrg?.member,
                            )
                          }
                        />
                      </MenuItem>
                    )}
                    {property.proformaFile && (
                      <MenuItem value={'uploaded'}>
                        <ListItemText primary="Uploaded File" />
                      </MenuItem>
                    )}
                    <MenuItem value={'CMA'}>
                      <ListItemText primary="CMA" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {type !== 'CMA' && type !== 'uploaded' && (
                <Grid size={12}>
                  <FormControlLabel
                    key="proformaImagePage"
                    control={
                      <Checkbox
                        name="proformaImagePage"
                        value={emailImagePage}
                        icon={
                          <CheckBoxOutlineBlankIcon fontSize="large" />
                        }
                        checkedIcon={
                          <CheckBoxIcon
                            fontSize="large"
                            style={{
                              color: '#2296F3'}}
                          />
                        }
                        checked={emailImagePage}
                        onClick={handleEmailImagePageChange}
                        color="primary"
                        style={{ marginTop: 'auto' }}
                      />
                    }
                    label="Include all property images on the end of the report"
                  />
                </Grid>
              )}
              {session.me?.activeOrg.southernImpressions && (
                <Grid size={12}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={[
                      {
                        label: 'LPS FAQ',
                        value:
                          'https://housefolios-southernimpressionhomes.s3.us-west-1.amazonaws.com/LowPressureSystemFAQ.pdf'},
                      {
                        label: 'PBTS info',
                        value:
                          'https://housefolios-southernimpressionhomes.s3.us-west-1.amazonaws.com/PBTSInformationalFlyer.pdf'},
                      {
                        label:
                          'Sprinklers and Extinguishers Inspections',
                        value:
                          'https://housefolios-southernimpressionhomes.s3.us-west-1.amazonaws.com/QuadFireSprinklersAndExtinguishersInspectionsFlyer.pdf'},
                    ]}
                    filterSelectedOptions
                    fullWidth={true}
                    value={flyers}
                    onChange={handleFlyersChange}
                    isOptionEqualToValue={isEqual}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Additional Flyers"
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              disabled={!state.dataURL}
              variant="contained"
              color="primary"
              type="submit"
              startIcon={<SendIcon />}
            >
              Send
            </Button>
          </DialogActions>
        </ValidatorForm>
      </BlockUi>
    </Dialog>
  )
}

export default EmailDialog
